/**
 * Formats a price with the specified currency using Intl.NumberFormat.
 *
 * @param {number} price - The price to format.
 * @param {string} [currency] - The currency to use, defaults to USD.
 * @returns {string} The formatted price.
 */

export const dynamicPriceFormatter = (price: number, currency?: string) => {
  // Handle null, undefined, or invalid values
  if (price == null || price === undefined) return "0";

  // Convert to number if it's a string or other type
  const numPrice = typeof price === "number" ? price : Number(price);

  // Handle NaN or invalid numbers
  if (isNaN(numPrice) || !isFinite(numPrice)) return "0";

  // price formatter.
  var formatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency ? currency : "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  if (numPrice === 0 || numPrice === 1) {
    return formatter.format(numPrice);
  }
  return numPrice > 1.01
    ? formatter.format(numPrice)
    : -Math.floor(Math.log(numPrice) / Math.log(10) + 1) < 4
    ? formatter.format(numPrice).replace(/[\d., ]/g, "") + numPrice.toFixed(6)
    : formatter.format(numPrice).replace(/[\d., ]/g, "") + numPrice.toFixed(10);
};

/**
 * Formats a unit with the specified price using Intl.NumberFormat.
 *
 * @param {number} unit - The unit to format.
 * @param {number} price - The price to use for formatting.
 * @returns {string} The formatted unit.
 */

export const dynamicUnitFormatter = (unit: number, price: number) => {
  const decimal = unit % 1;
  const integer = Math.round(unit);

  const formatter = new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 8,
  });

  // first check if number is negative
  if (Math.sign(unit) === -1) {
    return 0;
  }

  // check if units have decimals figures
  if (decimal) {
    // if price is larger then 10,000, show units with four decimal places
    if (price > 10000) {
      return formatter.format(parseFloat(unit.toFixed(4)));
    }
    // check if fractional part of decimal starts with 0
    const zeros = -Math.floor(Math.log10(decimal) + 1);
    if (zeros) {
      // if 4 or more 0 zeros, display units with 8 decimal places
      if (zeros >= 4) {
        return formatter.format(parseFloat(unit.toFixed(8)));
        // if 2 or more, display units with 6 decimal places
      } else if (zeros >= 2) {
        return formatter.format(parseFloat(unit.toFixed(6)));
        // else display 4 decimal places
      } else {
        return formatter.format(parseFloat(unit.toFixed(4)));
      }
    }
    if (integer < 10 && integer > 0) {
      return formatter.format(parseFloat(unit.toFixed(4)));
    } else {
      return formatter.format(parseFloat(unit.toFixed(2)));
    }
  }

  return formatter.format(unit);
};

// Format price to human readable format
// e.g. 5776.36 => $ A$5,776.38 for AUD currency

export type FormatCurrencyOptions = {
  type?: string;
};

/**
 * Formats a number as a currency string with various options.
 * @param {number} price - The number to format.
 * @param {string} currency - The currency code to use for formatting.
 * @param {FormatCurrencyOptions} [options] - Options to customize the formatting.
 * @returns {string} The formatted currency string.
 */
export const formatCurrency = (
  price: number,
  currency: string,
  options?: FormatCurrencyOptions,
) => {
  // put BTC formatting at the top so it overrides others

  if (currency === "BTC") {
    // when converting to BTC, the number will be very small, so use sig figs to ensure numbers are somewhat visible.
    // also the currency needs to be a suffix instead of prefix.
    if (price === 0) {
      return `0 BTC`;
    } else if (price < Math.pow(10, -6)) {
      return `<${Math.pow(10, -6)} BTC`;
    }
    return `${price.toPrecision(6)} BTC`;
  }
  if (options && options.type === "none") {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(price);
  }
  if (options && options.type === "name") {
    const formattedPrice = new Intl.NumberFormat("en-US", {
      notation: "compact",
      compactDisplay: "long",
    }).format(price);
    const number = formattedPrice.split(" ")[0];
    const name = formattedPrice.split(" ")[1];
    // capitlaizing the first letter of the word
    return "$" + number + " " + name?.charAt(0).toUpperCase() + name?.slice(1);
  }
  if (options && options.type === "format") {
    // Generate a formatter to get the locale-specific currency symbol
    const formatter = new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency,
      minimumFractionDigits: 0, // Avoid additional decimals for the extraction
    });

    // Use the formatter to format a dummy number and extract the currency symbol
    const dummyNumber = 0;
    const formattedDummy = formatter.format(dummyNumber);
    let symbol = formattedDummy.replace(dummyNumber.toString(), "").trim();

    // Determine if the number is an integer or if it has less than two decimal places
    const priceString = price.toString();
    const decimalPart = priceString.split(".")[1];
    let formattedPrice = priceString;

    // If there's no decimal part, add ".00"
    if (decimalPart === undefined) {
      formattedPrice += ".00";
    } else if (decimalPart.length === 1) {
      // If there's one decimal place, add "0"
      formattedPrice += "0";
    } // If there are two or more decimal places, leave it as is

    // Concatenate the currency symbol with the conditionally formatted price
    return `${symbol}${formattedPrice}`;
  }

  if (options && options.type === "round") {
    const formattedPrice = new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency,
    }).format(price);
    const currencyNotAustralian = formattedPrice.toString()[0] !== "A";
    if (currencyNotAustralian) {
      return formattedPrice.substring(0, formattedPrice.length - 3).replace(/(\.|,)00$/g, "");
    }
    return formattedPrice.substring(1, formattedPrice.length - 3).replace(/(\.|,)00$/g, "");
  }
  if (options && options.type === "$") {
    const formattedPrice = new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency,
    }).format(price);

    const currencyNotAustralian = formattedPrice.toString()[0] !== "A";
    if (currencyNotAustralian) {
      return formattedPrice.replace(/(\.|,)00$/g, "");
    }
    // REMOVE CURRENCY PREFIX E.G. A$50,821.49 => $50,821.49
    return formattedPrice
      .toString()
      .substring(1)
      .replace(/(\.|,)00$/g, "");
  }
  var formatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency ? currency : "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  if (typeof price === "undefined") return "0";

  if (price === 0 || price === 1) {
    return formatter.format(price);
  }

  if (!price) return formatter.format(price); // toFixed will throw an error if price is undefined

  return price > 1
    ? formatter.format(price)
    : -Math.floor(Math.log(price) / Math.log(10) + 1) < 4
    ? formatter.format(price).replace(/[\d., ]/g, "") + price.toFixed(4)
    : formatter.format(price).replace(/[\d., ]/g, "") + price.toFixed(7);
};

/**
 * Formats a number by adding commas to separate thousands.
 *
 * @param {any} number - The number to format.
 * @returns {string} The formatted number as a string.
 */
export const numberFormatter = (number: any) => {
  return new Intl.NumberFormat("en-US").format(number);
};

/**
 * Formats a number with commas according to the en-US locale.
 *
 * @param {any} number - The number to format.
 * @returns {string} The formatted number string with commas.
 */
export const NumberCommaFormatter = (number: any) => {
  return Intl.NumberFormat("en-US").format(number);
};

/**
 * Formats the given change percentage as a string with a specified number of decimal places.
 *
 * @param {number} change - The change percentage to format.
 * @param {number} pos - The number of decimal places to include in the formatted string.
 * @returns {string} The formatted change percentage as a string.
 */
export function formatChange(change: number, pos: number): string {
  return `${(change * 100).toFixed(pos)}%`;
}
/**
 * Returns a number formatter that formats numbers as currency in USD.
 * @param {number} [numberOfDecimals] - The number of decimal places to display. Defaults to 6.
 * @returns {Intl.NumberFormat} The number formatter.
 */
export const formatter = (numberOfDecimals?: number) => {
  return new Intl.NumberFormat("en-US", {
    maximumSignificantDigits: numberOfDecimals ? numberOfDecimals : 6,
    style: "currency",
    currency: "USD",
  });
};

// SAFE ROUNDING round units down.
// function prevents the user sending more holding than the own to netsuite
// this matches netsuite that used units to 4 decimals.

export const truncateTo4Decimals = (num: number = 0): string => {
  const numAsString = num.toString();
  const decimalIndex = numAsString.indexOf(".");
  if (decimalIndex === -1) {
    return numAsString;
  }
  return numAsString.slice(0, decimalIndex + 1 + 4);
};

export const countSignificantDecimalPlaces = (numberOrScientificNotation: number): number => {
  const input = Number(numberOrScientificNotation).toFixed(10);
  if (+input >= 100) {
    return 2; // Display without decimal places
  }
  const decimalPart = input.toString().split(".")[1];

  if (!decimalPart) {
    return 0; // No decimal part
  }

  let count = 0;
  for (let i = decimalPart.length - 1; i >= 0; i--) {
    if (decimalPart[i] !== "0") {
      break; // Stop counting at the first non-zero digit
    }
    count++;
  }

  return decimalPart.length - count;
};
